import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import type { RefObject, TextareaHTMLAttributes } from 'react';

interface AutoTextareaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'> {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  rows?: number;
  /** React 19: ref is a regular prop — callers may need the DOM node (e.g. the
   *  promo bio formatting bar reads selectionStart/selectionEnd). */
  ref?: RefObject<HTMLTextAreaElement | null>;
}

/**
 * Single-line-by-default textarea that auto-grows to fit its content, so
 * long values are never truncated like a fixed <input type="text">.
 *
 * THREE resize triggers (2026-11-04, LOT N1) — a value change alone is NOT
 * enough, and the measured failures are the reason:
 *
 *   1. `value` changed → grow/shrink on type, paste, programmatic load.
 *   2. the element's BOX changed → `ResizeObserver`. Most note fields live in a
 *      `<CardStack foldable>`, i.e. a `<details className="card">`. While that
 *      details is CLOSED the textarea is not laid out and reports
 *      `scrollHeight === 0`; on open `value` is unchanged, so a value-only
 *      effect never runs again and the field stayed 2 rows high with a
 *      scrollbar (user report on show 1749: a 492-char note cut to 1/5th).
 *      Writing `height: 0px` in that state is worse — hence the guard below.
 *   3. an ancestor `<details>` TOGGLED → `toggle` (non-bubbling, so listened
 *      per details element) in case the box metrics do not move in every
 *      browser. Triggers 2 and 3 both just call `resize()`, which is idempotent.
 *
 * `resize()` NEVER writes `0px`: when `scrollHeight` is 0 (hidden / closed
 * details / display:none) it falls back to `height: auto` so the field can
 * never be frozen at zero height.
 */
export default function AutoTextarea({ value, onChange, rows = 1, className, ref: externalRef, ...rest }: AutoTextareaProps) {
  const innerRef = useRef<HTMLTextAreaElement>(null);
  const ref = externalRef ?? innerRef;
  /** Last observed WIDTH — a width change re-wraps the text, hence a new height. */
  const lastWidth = useRef(0);

  const resize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    lastWidth.current = el.clientWidth;
    // Reset first so `scrollHeight` reflects the CONTENT, not the previous height.
    el.style.height = 'auto';
    const sh = el.scrollHeight;
    if (sh <= 0) {
      // Hidden (closed <details>, display:none): never freeze a 0px height.
      return;
    }
    el.style.height = `${sh}px`;
  }, [ref]);

  // Layout effect: resize before paint so a loaded note never flashes truncated.
  useLayoutEffect(() => {
    resize();
  }, [value, resize]);

  // Any box change (window/card resize, closed → open details) re-measures.
  // Guard: the height WE write also fires the observer — only react to a WIDTH
  // change or to a zero box (hidden → shown), never to our own height write,
  // otherwise the observer feeds itself (RO loop warning, CPU spin).
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      const width = entry ? entry.contentRect.width : el.clientWidth;
      const height = entry ? entry.contentRect.height : el.clientHeight;
      if (height > 0 && Math.abs(width - lastWidth.current) < 1) return;
      resize();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, resize]);

  // `toggle` does not bubble → one listener per ancestor <details>.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const bound: Array<{ node: Element; fn: () => void }> = [];
    let node: HTMLElement | null = el.parentElement;
    while (node) {
      if (node.tagName === 'DETAILS') {
        const fn = () => resize();
        node.addEventListener('toggle', fn);
        bound.push({ node, fn });
      }
      node = node.parentElement;
    }
    if (bound.length === 0) return;
    return () => bound.forEach(({ node: n, fn }) => n.removeEventListener('toggle', fn));
  }, [ref, resize]);

  return (
    <textarea
      ref={ref}
      value={value}
      onChange={onChange}
      rows={rows}
      className={className ? `auto-textarea ${className}` : 'auto-textarea'}
      {...rest}
    />
  );
}
